import React, { useEffect, useRef } from "react";

const HERO_ART =
  "https://customer-assets.emergentagent.com/job_aa602633-f1be-47d7-973a-0c2373de9f76/artifacts/njlmdoc7_icm_fullxfull.896202731_kq6udl6lktw8scoowwww.jpg";

export default function Hero() {
  const artRef = useRef(null);

  // Subtle parallax tied to scroll (desktop only)
  useEffect(() => {
    const el = artRef.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const y = Math.min(window.scrollY, 600);
        el.style.transform = `translate3d(0, ${y * 0.08}px, 0)`;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-ocean-base"
      aria-label="Against All Waves hero"
    >
      {/* Character art - desktop: right side; mobile: behind content */}
      <div
        ref={artRef}
        className="absolute inset-0 md:left-auto md:right-0 md:w-[58%] lg:w-[55%] h-full pointer-events-none will-change-transform overflow-hidden z-[1]"
        aria-hidden="false"
      >
        <img
          src={HERO_ART}
          alt="Character art for Against All Waves, a young adult pirate fantasy romance"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="w-full h-full object-cover object-center float-soft ken-burns"
          style={{ filter: "saturate(0.85) contrast(1.05) brightness(0.78)" }}
        />
        {/* Cinematic gradient overlay */}
        <div className="absolute inset-0 hero-overlay" />
      </div>

      {/* Aurora glow at the back */}
      <div className="aurora" aria-hidden="true">
        <span className="aurora-blob aurora-blob--teal" />
        <span className="aurora-blob aurora-blob--gold" />
        <span className="aurora-blob aurora-blob--storm" />
      </div>

      {/* Animated layers */}
      <div className="mist" aria-hidden="true" />
      <div className="stars" aria-hidden="true" />
      <div className="constellation" aria-hidden="true" />
      <div className="embers" aria-hidden="true">
        {Array.from({ length: 14 }).map((_, i) => (
          <span key={i} style={{ "--i": i }} />
        ))}
      </div>

      {/* Floating bokeh orbs across the hero (above the character art) */}
      <div className="orbs" aria-hidden="true">
        {Array.from({ length: 10 }).map((_, i) => (
          <span key={i} style={{ "--i": i }} />
        ))}
      </div>
      <div className="parchment-overlay absolute inset-0" aria-hidden="true" />
      <div className="vignette" aria-hidden="true" />

      {/* Animated SVG waves at the bottom of the hero */}
      <div className="hero-waves" aria-hidden="true">
        <svg
          viewBox="0 0 1440 200"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient id="waveGradA" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="rgba(125,180,181,0)" />
              <stop offset="100%" stopColor="rgba(125,180,181,0.18)" />
            </linearGradient>
            <linearGradient id="waveGradB" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="rgba(28,43,57,0)" />
              <stop offset="100%" stopColor="rgba(10,17,24,0.85)" />
            </linearGradient>
            <linearGradient id="waveGradC" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="rgba(201,162,90,0)" />
              <stop offset="100%" stopColor="rgba(201,162,90,0.18)" />
            </linearGradient>
          </defs>
          <path
            className="wave-path wave-a"
            d="M0,120 C180,80 360,160 540,120 C720,80 900,160 1080,120 C1260,80 1440,140 1620,120 L1620,200 L0,200 Z"
            fill="url(#waveGradA)"
          />
          <path
            className="wave-path wave-b"
            d="M0,140 C200,100 400,180 600,140 C800,100 1000,180 1200,140 C1400,100 1600,160 1800,140 L1800,200 L0,200 Z"
            fill="url(#waveGradC)"
            opacity="0.65"
          />
          <path
            className="wave-path wave-c"
            d="M0,160 C160,140 320,190 480,160 C640,130 800,190 960,160 C1120,130 1280,180 1440,160 L1440,200 L0,200 Z"
            fill="url(#waveGradB)"
          />
        </svg>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 lg:px-16 min-h-screen flex items-center pt-32 pb-24">
        <div className="w-full md:w-[52%] lg:w-1/2 xl:w-[46%] max-w-[640px]">
          <div className="flex items-center gap-3 fade-in delay-200">
            <span className="hero-eyebrow-line" aria-hidden="true" />
            <p
              data-testid="hero-eyebrow"
              className="font-accent text-[10px] md:text-xs text-[#c9a25a]"
            >
              A Novel by Chelsea Ray
            </p>
          </div>

          <h1
            data-testid="hero-title"
            aria-label="Against All Waves"
            className="font-display mt-6 text-5xl sm:text-6xl md:text-[4.5rem] lg:text-[5rem] xl:text-[5.5rem] leading-[0.95] text-[#f2e8d5] fade-in-up delay-400 break-words"
          >
            <span className="block">Against</span>
            <span className="block">
              <span className="italic gold-shimmer">All</span> Waves
            </span>
          </h1>

          <div
            data-testid="hero-quote"
            className="mt-10 max-w-lg fade-in-slow delay-1000 quote-ripple"
          >
            <span className="block font-display text-[#c9a25a]/80 text-3xl leading-none mb-2">
              &ldquo;
            </span>
            <blockquote className="font-display italic text-xl sm:text-2xl md:text-[1.5rem] leading-relaxed text-[#e8d9b6]">
              He repeats my name, &lsquo;Eleanor,&rsquo; like he is savoring
              it. Like I have told him a secret meant only for the two of us.
            </blockquote>
          </div>

          <p
            data-testid="hero-tagline"
            className="mt-10 max-w-lg text-base md:text-[1.02rem] text-[#a8b4c0] leading-relaxed fade-in delay-1400"
          >
            A young adult pirate fantasy of slow-burn romance, deadly seas,
            found family, and secrets worth crossing oceans for.
          </p>

          <div
            data-testid="hero-ctas"
            className="mt-12 flex flex-col sm:flex-row gap-4 fade-in delay-1800"
          >
            <a
              data-testid="hero-cta-book"
              href="#book"
              className="btn-gold btn-gold-solid"
              aria-label="Read more about Against All Waves"
            >
              Discover the Book
            </a>
            <a
              data-testid="hero-cta-newsletter"
              href="#newsletter"
              className="btn-gold"
              aria-label="Join the Against All Waves newsletter"
            >
              Join the Crew
            </a>
          </div>

          <p
            data-testid="hero-art-credit"
            className="mt-12 font-accent text-[9px] text-[#a8b4c0]/50 fade-in delay-1800"
          >
            Character art by Nora Adamszki
          </p>
        </div>
      </div>

      {/* Bottom scroll cue */}
      <div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 fade-in delay-1800 hidden md:flex flex-col items-center gap-2 text-[#c9a25a]/70"
        aria-hidden="true"
      >
        <span className="font-accent text-[9px]">Scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-[#c9a25a]/70 to-transparent" />
      </div>
    </section>
  );
}
